const chapter = {
  id: 'breathing-exchange-gases',
  slug: 'breathing-exchange-gases',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Breathing and Exchange of Gases',
        hindi: 'श्वासोच्छ्वास और गैसों का विनिमय',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Human Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'respiratory-organs',
      title: {
        english: 'Respiratory Organs in Animals',
        hindi: 'प्राणियों में श्वसन अंग',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Lower invertebrates (sponges, cnidarians, flatworms): direct exchange across body surface (diffusion).',
        hindi: 'निचले अकशेरुकी (स्पंज, निडारियन, फ्लैटवर्म): शरीर की सतह पर सीधा आदान-प्रदान (प्रसार)।',
            },
            {
              english: 'Earthworm: moist cuticle.',
        hindi: 'केंचुआ: नम छल्ली.',
            },
            {
              english: 'Insects: **tracheal system** with spiracles — air directly to tissues.',
        hindi: 'कीड़े: ** श्वासनली प्रणाली** श्वासयंत्र के साथ - ऊतकों तक सीधे हवा।',
            },
            {
              english: 'Aquatic vertebrates (fish): **gills** (gill filaments with lamellae) — countercurrent flow.',
        hindi: 'जलीय कशेरुक (मछली): **गिल्स** (लैमेला के साथ गिल फिलामेंट्स) - प्रतिधारा प्रवाह।',
            },
            {
              english: 'Terrestrial vertebrates: **lungs** — internal vascularised sacs.',
        hindi: 'स्थलीय कशेरुक: **फेफड़े** - आंतरिक संवहनी थैली।',
            },
          ],
        },
      ],
    },
    {
      id: 'human-respiratory',
      title: {
        english: 'Human Respiratory System',
        hindi: 'मानव श्वसन तंत्र',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Conducting part**: external nares → nasal chamber → pharynx → larynx → trachea → bronchi → bronchioles → terminal bronchioles. Cleans, warms, humidifies air.',
        hindi: '**संचालन भाग**: बाहरी नासिका → नासिका कक्ष → ग्रसनी → स्वरयंत्र → श्वासनली → ब्रांकाई → ब्रोन्किओल्स → टर्मिनल ब्रोन्किओल्स। हवा को साफ करता है, गर्म करता है, आर्द्र बनाता है।',
            },
            {
              english: '**Larynx** — voice box; covered by **epiglottis** during swallowing.',
        hindi: '**स्वरयंत्र** — ध्वनि बॉक्स; निगलने के दौरान **एपिग्लॉटिस** से ढका हुआ।',
            },
            {
              english: '**Trachea** — supported by C-shaped cartilage rings; bifurcates into 2 primary bronchi at level of T5.',
        hindi: '**ट्रेकिआ** - सी-आकार के उपास्थि वलय द्वारा समर्थित; T5 के स्तर पर 2 प्राथमिक ब्रांकाई में विभाजित हो जाता है।',
            },
            {
              english: '**Exchange part**: respiratory bronchioles → alveolar ducts → alveoli (~300 million; total area ~70-100 m²). Alveolar wall = single layer of squamous epithelium.',
        hindi: '**विनिमय भाग**: श्वसन ब्रोन्किओल्स → वायुकोशीय नलिकाएं → वायुकोशिका (~300 मिलियन; कुल क्षेत्रफल ~70-100 वर्ग मीटर)। वायुकोशीय wall = single layer of squamous epithelium.',
            },
            {
              english: '**Lungs** — covered by **pleura** (double layer) with pleural fluid (lubrication). Right lung = 3 lobes, left = 2 lobes (cardiac notch).',
        hindi: '**फेफड़े** - फुफ्फुस द्रव (स्नेहन) के साथ **फुफ्फुस** (दोहरी परत) से ढके होते हैं। दाएँ lung = 3 lobes, left = 2 lobes (cardiac notch)।',
            },
            {
              english: '**Diaphragm** — dome-shaped skeletal muscle separating thorax & abdomen; primary inspiratory muscle.',
        hindi: '**डायाफ्राम** - वक्ष और पेट को अलग करने वाली गुंबद के आकार की कंकाल मांसपेशी; प्राथमिक श्वसन मांसपेशी.',
            },
          ],
        },
      ],
    },
    {
      id: 'mechanism',
      title: {
        english: 'Mechanism of Breathing',
        hindi: 'श्वासोच्छ्वास की क्रियाविधि',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Two phases: **Inspiration** (active) & **Expiration** (passive at rest).',
        hindi: 'दो चरण: **प्रेरणा** (सक्रिय) और **समाप्ति** (आराम के समय निष्क्रिय)।',
            },
            {
              english: '**Inspiration**: diaphragm contracts (flattens) → external intercostals raise ribs & sternum → thoracic volume ↑ → intra-pulmonary pressure < atmospheric (~−1 mmHg) → air rushes in.',
        hindi: '**प्रेरणा**: डायाफ्राम सिकुड़ता है (चपटा होता है) → बाहरी इंटरकोस्टल पसलियों और उरोस्थि को ऊपर उठाते हैं → वक्षीय आयतन ↑ → इंट्रा-फुफ्फुसीय दबाव <वायुमंडलीय (~−1 mmHg) → हवा अंदर आती है।',
            },
            {
              english: '**Expiration**: diaphragm relaxes (domes up) → ribs return → volume ↓ → pressure > atmospheric (~+1 mmHg) → air goes out.',
        hindi: '**समाप्ति**: डायाफ्राम शिथिल हो जाता है (गुंबद ऊपर की ओर) → पसलियां वापस आती हैं → आयतन ↓ → दबाव > वायुमंडलीय (~+1 mmHg) → हवा बाहर जाती है।',
            },
            {
              english: 'Normal breathing rate: **12-16 / min** in healthy adult.',
        hindi: 'सामान्य श्वास दर: स्वस्थ वयस्कों में **12-16/मिनट**।',
            },
            {
              english: '**Spirometer** measures lung volumes.',
        hindi: '**स्पाइरोमीटर** फेफड़ों की मात्रा मापता है।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Volume / Capacity', 'Definition', 'Value (mL)'],
          rows: [
            ['Tidal Volume (TV)', 'Air inspired/expired in normal breath', '500'],
            ['Inspiratory Reserve Volume (IRV)', 'Extra air forcibly inspired', '2500-3000'],
            ['Expiratory Reserve Volume (ERV)', 'Extra air forcibly expired', '1000-1100'],
            ['Residual Volume (RV)', 'Air left after forceful expiration', '1100-1200'],
            ['Inspiratory Capacity (IC)', 'TV + IRV', '3000-3500'],
            ['Expiratory Capacity (EC)', 'TV + ERV', '1500-1600'],
            ['Functional Residual Capacity (FRC)', 'ERV + RV', '2100-2300'],
            ['Vital Capacity (VC)', 'IRV + TV + ERV', '3500-4500'],
            ['Total Lung Capacity (TLC)', 'VC + RV', '6000'],
          ],
        },
      ],
    },
    {
      id: 'exchange',
      title: {
        english: 'Exchange & Transport of Gases',
        hindi: 'गैसों का विनिमय और परिवहन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Exchange occurs by **simple diffusion** based on partial pressure (pO₂, pCO₂) gradients.',
        hindi: 'विनिमय आंशिक दबाव (pO₂, pCO₂) ग्रेडिएंट के आधार पर **सरल प्रसार** द्वारा होता है।',
            },
            {
              english: 'Partial pressures (mmHg):',
        hindi: 'आंशिक दबाव (mmHg):',
            },
            {
              english: '• Atmospheric: pO₂ = 159, pCO₂ = 0.3.',
        hindi: '• वायुमंडलीय: pO₂ = 159, pCO₂ = 0.3.',
            },
            {
              english: '• Alveoli: pO₂ = 104, pCO₂ = 40.',
        hindi: '• एल्वियोली: pO₂ = 104, pCO₂ = 40।',
            },
            {
              english: '• Deoxygenated blood: pO₂ = 40, pCO₂ = 45.',
        hindi: '• ऑक्सीजन रहित रक्त: pO₂ = 40, pCO₂ = 45।',
            },
            {
              english: '• Oxygenated blood: pO₂ = 95, pCO₂ = 40.',
        hindi: '• ऑक्सीजन युक्त रक्त: pO₂ = 95, pCO₂ = 40।',
            },
            {
              english: '• Tissues: pO₂ = 40, pCO₂ = 45.',
        hindi: '• ऊतक: pO₂ = 40, pCO₂ = 45।',
            },
            {
              english: 'Alveolar membrane is **0.2 µm** thick — efficient diffusion.',
        hindi: 'वायुकोशीय झिल्ली **0.2 µm** मोटी - कुशल प्रसार है।',
            },
            {
              english: 'Solubility of CO₂ ≈ **20-25× more** than O₂ → CO₂ diffuses faster.',
        hindi: 'CO₂ की घुलनशीलता O₂ की तुलना में ≈ **20-25× अधिक** है → CO₂ तेजी से फैलती है।',
            },
            {
              english: '**Transport of O₂** — 97 % bound to **haemoglobin** as oxyhaemoglobin (each Hb binds 4 O₂), 3 % dissolved in plasma. Each gram Hb binds ~1.34 mL O₂.',
        hindi: '**O₂** का परिवहन - 97% ऑक्सीहीमोग्लोबिन के रूप में **हीमोग्लोबिन** से बंधा होता है (प्रत्येक Hb 4 O₂ को बांधता है), 3% प्लाज्मा में घुल जाता है। प्रत्येक ग्राम एचबी ~1.34 एमएल ओ₂ को बांधता है।',
            },
            {
              english: '**Oxygen-haemoglobin dissociation curve** = sigmoid. Right shift (Bohr effect) by ↑ pCO₂, ↑ H⁺ (↓ pH), ↑ temperature, ↑ 2,3-BPG → easier O₂ release at tissues.',
        hindi: '**ऑक्सीजन-हीमोग्लोबिन पृथक्करण वक्र** = सिग्मॉइड। राइट शिफ्ट (बोह्र प्रभाव) ↑ pCO₂, ↑ H⁺ (↓ pH), ↑ तापमान, ↑ 2,3-BPG → ऊतकों पर आसान O₂ रिलीज।',
            },
            {
              english: '**Transport of CO₂**:',
        hindi: '**CO₂ का परिवहन**:',
            },
            {
              english: '• ~7 % dissolved in plasma.',
        hindi: '• ~7% प्लाज्मा में घुला हुआ।',
            },
            {
              english: '• ~20-25 % as carbamino-haemoglobin (bound to globin).',
        hindi: '• ~20-25% कार्बामिनो-हीमोग्लोबिन (ग्लोबिन से बंधा हुआ) के रूप में।',
            },
            {
              english: '• ~70 % as **bicarbonate (HCO₃⁻)** in plasma — carbonic anhydrase in RBC catalyses $CO_2 + H_2O \\rightleftharpoons H_2CO_3 \\rightleftharpoons H^+ + HCO_3^-$. Chloride shift (Hamburger\'s phenomenon) maintains balance.',
        hindi: '• ~70% प्लाज्मा में **बाइकार्बोनेट (HCO₃⁻)** के रूप में - RBC में कार्बोनिक एनहाइड्रेज़ $CO_2 + H_2O \\rightleftharpoons H_2CO_3 \\rightleftharpoons H^+ + HCO_3^-$ को उत्प्रेरित करता है। क्लोराइड शिफ्ट (हैमबर्गर घटना) संतुलन बनाए रखता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'regulation-disorders',
      title: {
        english: 'Regulation & Disorders',
        hindi: 'नियमन और विकार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Respiratory centre** — in **medulla oblongata**; **pneumotaxic centre in pons** modulates rhythm.',
        hindi: '**श्वसन केंद्र** — **मेडुला ऑबोंगटा** में; **पोंस में न्यूमोटैक्सिक केंद्र** लय को नियंत्रित करता है।',
            },
            {
              english: 'Chemoreceptors in aortic arch & carotid arteries sense ↑ CO₂ / H⁺ → ↑ breathing.',
        hindi: 'महाधमनी चाप और कैरोटिड धमनियों में केमोरिसेप्टर ↑ CO₂ / H⁺ → ↑ श्वास का अनुभव करते हैं।',
            },
            {
              english: '**Disorders**:',
        hindi: '**विकार**:',
            },
            {
              english: '• **Asthma** — bronchial spasm; difficulty in breathing, wheezing.',
        hindi: '• **अस्थमा** — ब्रोन्कियल ऐंठन; सांस लेने में कठिनाई, घरघराहट।',
            },
            {
              english: '• **Emphysema** — alveolar walls damaged (↓ surface area); chronic smoking.',
        hindi: '• **वातस्फीति** - वायुकोशीय दीवारें क्षतिग्रस्त (↓ सतह क्षेत्र); दीर्घकालिक धूम्रपान.',
            },
            {
              english: '• **Occupational pulmonary diseases** — silicosis, asbestosis (long exposure to dust).',
        hindi: '• **व्यावसायिक फुफ्फुसीय रोग** - सिलिकोसिस, एस्बेस्टॉसिस (धूल के संपर्क में लंबे समय तक रहना)।',
            },
            {
              english: '• **Pneumonia** — alveoli filled with fluid; bacterial (Streptococcus).',
        hindi: '• **निमोनिया** — द्रव से भरी वायुकोशिका; जीवाणु (स्ट्रेप्टोकोकस)।',
            },
            {
              english: '• **Tuberculosis (TB)** — Mycobacterium tuberculosis.',
        hindi: '• **ट्यूबरकुलोसिस (टीबी)** — माइकोबैक्टीरियम ट्यूबरकुलोसिस।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'TV = 500\\ \\text{mL}; \\text{ Breathing rate} = 12\\text{-}16/\\min',
      desc: {
        english: '',
        hindi: 'टीवी = 500\\ एमएल; श्वास दर = 12-16/',
      },
    },
    {
      latex: 'VC = IRV + TV + ERV',
      desc: {
        english: '',
        hindi: 'वीसी = आईआरवी + टीवी + ईआरवी',
      },
    },
    {
      latex: 'TLC = VC + RV \\approx 6000\\ \\text{mL}',
      desc: {
        english: '',
        hindi: 'टीएलसी = वीसी + आरवी 6000\\ एमएल',
      },
    },
    {
      latex: 'CO_2 + H_2O \\rightleftharpoons H_2CO_3 \\rightleftharpoons H^+ + HCO_3^-',
      desc: {
        english: '',
        hindi: 'सीओ 2 + एच 2ओ एच 2सीओ 3 एच + + एचसीओ 3 -',
      },
    },
    {
      latex: '97\\% \\text{ O}_2 \\text{ via Hb}; 70\\% \\text{ CO}_2 \\text{ as HCO}_3^-',
      desc: {
        english: '',
        hindi: 'एचबी के माध्यम से 97\\% ओ 2; 70\\% CO 2 HCO 3 के रूप में -',
      },
    },
    {
      latex: '\\text{Bohr effect: } \\downarrow pH \\Rightarrow \\text{right shift}',
      desc: {
        english: '',
        hindi: 'बोह्र प्रभाव: पीएच दायां बदलाव',
      },
    },
    {
      latex: '\\text{Respiratory centre: medulla oblongata}',
      desc: {
        english: '',
        hindi: 'श्वसन केंद्र: मेडुला ऑबोंगटा',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Volume',
      problem: {
        english: 'Vital Capacity equals:',
        hindi: 'महत्वपूर्ण क्षमता बराबर है:',
      },
      answer: {
        english: 'IRV + TV + ERV',
        hindi: 'आईआरवी + टीवी + ईआरवी',
      },
      solution: {
        english: 'Maximum volume of air a person can breathe in/out.',
        hindi: 'एक व्यक्ति द्वारा सांस ली जा सकने वाली हवा की अधिकतम मात्रा in/out है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Transport',
      problem: {
        english: 'Major form of CO₂ transport in blood:',
        hindi: 'रक्त में CO₂ परिवहन का प्रमुख रूप:',
      },
      answer: {
        english: 'Bicarbonate (HCO₃⁻)',
        hindi: 'बाइकार्बोनेट (HCO₃⁻)',
      },
      solution: {
        english: '~70 %; via carbonic anhydrase.',
        hindi: '~70 %; कार्बोनिक एनहाइड्रेज़ के माध्यम से।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Disease',
      problem: {
        english: 'Damage to alveolar walls in chronic smokers causes:',
        hindi: 'लगातार धूम्रपान करने वालों में वायुकोशीय दीवारों को नुकसान के कारण:',
      },
      answer: {
        english: 'Emphysema',
        hindi: 'वातस्फीति',
      },
      solution: {
        english: 'Reduces respiratory surface; breathlessness.',
        hindi: 'श्वसन सतह को कम करता है; सांस फूलना।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Bohr',
      problem: {
        english: 'Right shift of O₂-Hb curve is caused by:',
        hindi: 'O₂-Hb वक्र का दायां बदलाव किसके कारण होता है:',
      },
      answer: {
        english: '↑ CO₂, ↓ pH, ↑ temperature, ↑ 2,3-BPG',
        hindi: '↑ CO₂, ↓ pH, ↑ तापमान, ↑ 2,3-BPG',
      },
      solution: {
        english: 'Helps release O₂ at tissues.',
        hindi: 'ऊतकों पर O₂ जारी करने में मदद करता है।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Centre',
      problem: {
        english: 'Respiratory rhythm centre is located in:',
        hindi: 'श्वसन लय केंद्र स्थित है:',
      },
      answer: {
        english: 'Medulla oblongata',
        hindi: 'मेडुला ऑबोंगटा',
      },
      solution: {
        english: 'Pneumotaxic centre in pons modulates.',
        hindi: 'पोंस में न्यूमोटैक्सिक केंद्र मॉड्यूलेट होता है।',
      },
    },
  ],
  next: {
    slug: 'body-fluids-circulation',
    title: 'Body Fluids and Circulation',
  },
};

export default chapter;
